import { InputHTMLAttributes, Ref } from 'react';
import classnames from 'classnames';

import type { Icon } from '@personly/libs-types';

import { IconView } from '../index.js';

const makeInputClassName = (
  role: string,
  disabled: boolean,
  invalid: boolean
) =>
  classnames({
    'text-input-text-color': true,
    block: true,
    'h-10': true,
    'py-1.5': true,
    'px-2.5': true,
    'bg-clip-padding': true,
    rounded: true,
    border: true,
    'border-input-border-color': true,
    'focus:outline-none': true,
    'placeholder:text-input-placeholder-text-color': true,
    relative: true,
    'flex-auto': true,
    'min-w-0': true,
    'opacity-60': disabled,
    'outline-none': disabled,
    '!border-input-border-invalid-color': invalid,
    'focus:text-input-text-color': invalid,
    'focus:border-input-border-invalid-color': invalid,
    'bg-card-background-color': role === 'card',
    'focus:!bg-card-background-color': role === 'card',
    'bg-input-background-color': role !== 'card',
    'focus:!bg-input-background-color': role !== 'card',
  });

const className = classnames(
  'relative',
  'flex',
  'flex-wrap',
  'items-stretch',
  'w-full'
);

const makeButtonClassName = (role: string) =>
  classnames({
    flex: true,
    'justify-around': true,
    'items-center': true,
    'text-input-icon-color': true,
    'bg-card-background-color': role === 'card',
    'bg-input-background-color': role !== 'card',
  });

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  role?: string;
  disabled?: boolean;
  icon?: Icon;
  label: string;
  onClick?: () => void;
  ref?: Ref<HTMLButtonElement>;
  buttonType?: 'submit' | 'reset' | 'button';
  'data-testid'?: string;
}

const InputIcon = ({
  invalid = false,
  role = 'input',
  disabled = false,
  icon,
  label,
  onClick = () => {},
  ref,
  buttonType = 'button',
  ...props
}: Props) => (
  <div className={className}>
    <input
      readOnly={disabled}
      autoComplete="off"
      className={makeInputClassName(role, disabled, invalid)}
      disabled={disabled}
      {...props}
    ></input>
    <button
      aria-label={label}
      type={buttonType}
      onClick={() => onClick()}
      ref={ref}
      className={makeButtonClassName(role)}
    >
      <div className="absolute mr-10">
        <IconView icon={icon} size="1x" />
      </div>
    </button>
  </div>
);

InputIcon.displayName = 'InputIcon';

export default InputIcon;
